import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { format } from 'date-fns';
import {
  CalendarIcon,
  CheckIcon,
  ChevronsUpDownIcon,
  PlusIcon
} from 'lucide-react';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { CREATE_OPPORTUNITY, GET_DEAL_OPPORTUNITIES } from './queries';
import type { EmployeesAndDealSelects, GetAccounts } from './types';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/Dialog';
import { Badge } from '@/components/ui/badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText
} from '@/components/ui/input-group';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { cn, fileToBase64 } from '@/lib/utils';

interface NewDealDialogProps {
  data3: GetAccounts;
  dataEmployeesAndDealSelects: EmployeesAndDealSelects;
  hideTrigger?: boolean;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
}

const UPLOAD_CIM = gql`
  mutation UploadCim($id: ID!, $cim: String!) {
    uploadCim(opportunityId: $id, fileContent: $cim) {
      ok
    }
  }
`;

const UPLOAD_NDA = gql`
  mutation UploadNda($id: ID!, $nda: String!) {
    uploadNda(opportunityId: $id, fileContent: $nda) {
      ok
    }
  }
`;

const UPLOAD_TEASER = gql`
  mutation UploadTeaser($id: ID!, $teaser: String!) {
    uploadTeaser(opportunityId: $id, fileContent: $teaser) {
      ok
    }
  }
`;

export const NewDealDialog = ({
  data3,
  dataEmployeesAndDealSelects,
  hideTrigger,
  onOpenChange,
  open
}: NewDealDialogProps) => {
  const navigate = useNavigate();
  const [mutate, { loading: isCreatingOpportunity }] = useMutation(
    CREATE_OPPORTUNITY,
    {
      refetchQueries: [GET_DEAL_OPPORTUNITIES]
    }
  );
  const [uploadCim] = useMutation(UPLOAD_CIM);
  const [uploadNda] = useMutation(UPLOAD_NDA);
  const [uploadTeaser] = useMutation(UPLOAD_TEASER);

  const dealTeaserInputRef = useRef<HTMLInputElement | null>(null);

  const [companyName, setCompanyName] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [date, setDate] = useState<Date>();
  const [dealSource, setDealSource] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [newDealDialogOpen, setNewDealDialogOpen] = useState(false);
  const isControlled = typeof open === 'boolean';
  const dialogOpen = isControlled ? open : newDealDialogOpen;
  const handleOpenChange = (nextOpen: boolean) => {
    if (!isControlled) {
      setNewDealDialogOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
    if (!nextOpen) {
      resetNewDealForm();
    }
  };
  const [dealTeaserFile, setDealTeaserFile] = useState<File | null>(null);
  const [dealCimFile, setDealCimFile] = useState<File | null>(null);
  const [dealNdaFile, setDealNdaFile] = useState<File | null>(null);
  const dealCimInputRef = useRef<HTMLInputElement | null>(null);
  const dealNdaInputRef = useRef<HTMLInputElement | null>(null);
  const [emailBody, setEmailBody] = useState('');
  const [killNotes, setKillNotes] = useState('');
  const [killReason, setKillReason] = useState<string>();
  const [ltmEbitda, setLtmEbitda] = useState('');
  const [open1, setOpen1] = useState(false);
  const [open2, setOpen2] = useState(false);
  const [open3, setOpen3] = useState(false);
  const [open4, setOpen4] = useState(false);
  const [open5, setOpen5] = useState(false);
  const [opportunityNextStep, setOpportunityNextStep] = useState<string>();
  const [opportunityDetail, setOpportunityDetail] = useState('');
  const [projectName, setProjectName] = useState('');
  const [projectNameFieldIsInvalid, setProjectNameFieldIsInvalid] =
    useState(false);
  const [referencePoint, setReferencePoint] = useState('');
  const [smcIndustry, setSmcIndustry] = useState<{
    name: string;
    value: string;
  } | null>(null);
  const [smcSource, setSmcSource] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [smcSourceFieldIsInvalid, setSmcSourceFieldIsInvalid] = useState(false);
  const [smProjectedDebtInvestment, setSmProjectedDebtInvestment] =
    useState('');
  const [smProjectedEquityInvestment, setSmProjectedEquityInvestment] =
    useState('');
  const [sourceType, setSourceType] = useState<{
    id: string;
    name: string;
    value: string;
  } | null>(null);
  const [step, setStep] = useState<1 | 2>(1);
  const [totalProposedDebtInvestment, setTotalProposedDebtInvestment] =
    useState('');
  const [totalProposedEquityInvestment, setTotalProposedEquityInvestment] =
    useState('');

  const resetNewDealForm = () => {
    setCompanyName(null);
    setDate(undefined);
    setDealSource(null);
    setDealTeaserFile(null);
    setDealCimFile(null);
    setDealNdaFile(null);
    setEmailBody('');
    setKillNotes('');
    setKillReason(undefined);
    setLtmEbitda('');
    setOpen1(false);
    setOpen2(false);
    setOpen3(false);
    setOpen4(false);
    setOpen5(false);
    setOpportunityDetail('');
    setOpportunityNextStep(undefined);
    setProjectName('');
    setProjectNameFieldIsInvalid(false);
    setReferencePoint('');
    setSmProjectedDebtInvestment('');
    setSmProjectedEquityInvestment('');
    setSmcIndustry(null);
    setSmcSource(null);
    setSmcSourceFieldIsInvalid(false);
    setSourceType(null);
    setStep(1);
    setTotalProposedDebtInvestment('');
    setTotalProposedEquityInvestment('');
    if (dealTeaserInputRef.current) {
      dealTeaserInputRef.current.value = '';
    }
  };

  const handleLogNewDealButtonClick = async () => {
    const projectNameIsMissing = projectName.trim().length === 0;
    const smcSourceIsMissing = !smcSource;
    const formatDateForMutation = (dateValue?: Date) =>
      dateValue ? format(dateValue, 'yyyy-MM-dd') : undefined;
    const parseNumber = (value: string) => {
      const trimmedValue = value.trim();
      if (!trimmedValue) {
        return undefined;
      }

      const parsedValue = Number(trimmedValue);

      return Number.isNaN(parsedValue) ? undefined : parsedValue;
    };
    const formattedDate = formatDateForMutation(date);

    if (projectNameIsMissing) {
      setProjectNameFieldIsInvalid(true);
      return;
    }

    if (smcSourceIsMissing) {
      setSmcSourceFieldIsInvalid(true);
      return;
    }

    const response = await mutate({
      variables: {
        best_estimate_date: formattedDate,
        dealSourceId: dealSource?.id,
        dealSourceType: sourceType?.value ?? sourceType?.name,
        details: opportunityDetail || undefined,
        earliest_date: formattedDate,
        emailBody: emailBody || undefined,
        ioiDueDate: formattedDate,
        killedDealNotes: killNotes,
        killReason: killReason,
        latest_date: formattedDate,
        ltmEbitda: parseNumber(ltmEbitda),
        probabilty_of_close: undefined,
        projectName: projectName,
        referencePoint: referencePoint || undefined,
        sm_close_date_flexibility: undefined,
        smaller_enterprise: undefined,
        smcIndustry: smcIndustry?.value ?? smcIndustry?.name,
        smcSource: smcSource.id,
        smProjectedDebtInvestment: parseNumber(smProjectedDebtInvestment),
        smProjectedEquityInvestment: parseNumber(smProjectedEquityInvestment),
        status: opportunityNextStep,
        targetEntityId: companyName?.id,
        totalProposedDebtInvestment: parseNumber(totalProposedDebtInvestment),
        totalProposedEquityInvestment: parseNumber(
          totalProposedEquityInvestment
        )
      }
    });

    if (
      response.data &&
      typeof response.data === 'object' &&
      'createOpportunity' in response.data &&
      response.data.createOpportunity &&
      typeof response.data.createOpportunity === 'object' &&
      'ok' in response.data.createOpportunity &&
      response.data.createOpportunity.ok
    ) {
      const createOpportunityData = response.data.createOpportunity as {
        ok: boolean;
        opportunity?: { id: string };
      };
      const opportunityId = createOpportunityData.opportunity?.id;

      if (opportunityId) {
        // Upload CIM file if provided
        if (dealCimFile) {
          try {
            const base64Cim = await fileToBase64(dealCimFile);
            await uploadCim({
              variables: {
                cim: base64Cim,
                id: opportunityId
              }
            });
          } catch (error) {
            console.error('Error uploading CIM file:', error);
          }
        }

        // Upload NDA file if provided
        if (dealNdaFile) {
          try {
            const base64Nda = await fileToBase64(dealNdaFile);
            await uploadNda({
              variables: {
                id: opportunityId,
                nda: base64Nda
              }
            });
          } catch (error) {
            console.error('Error uploading NDA file:', error);
          }
        }

        // Upload Teaser file if provided
        if (dealTeaserFile) {
          try {
            const base64Teaser = await fileToBase64(dealTeaserFile);
            await uploadTeaser({
              variables: {
                id: opportunityId,
                teaser: base64Teaser
              }
            });
          } catch (error) {
            console.error('Error uploading Teaser file:', error);
          }
        }
      }

      // Navigate to the new opportunity and close dialog
      if (opportunityId) {
        handleOpenChange(false);
        resetNewDealForm();
        await navigate(`/investments/direct/${opportunityId}/overview`);
      } else {
        setNewDealDialogOpen(false);
        resetNewDealForm();
      }
    }
  };

  return (
    <Dialog
      onOpenChange={handleOpenChange}
      open={dialogOpen}
    >
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button>
            <PlusIcon />
            New Deal
          </Button>
        </DialogTrigger>
      )}
      <DialogContent aria-describedby={undefined}>
        <DialogHeader className="flex flex-row items-center">
          <DialogTitle>Create A New Deal</DialogTitle>
          <Badge variant="yellow">{'Unsaved'}</Badge>
        </DialogHeader>

        <DialogBody>
          <div className="grid max-h-full gap-4">
            <Breadcrumb>
              <BreadcrumbList>
                {step === 1 && (
                  <>
                    <BreadcrumbItem>
                      <BreadcrumbPage>Step 1</BreadcrumbPage>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>Step 2</BreadcrumbItem>
                  </>
                )}
                {step === 2 && (
                  <>
                    <BreadcrumbItem>Step 1</BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbPage>Step 2</BreadcrumbPage>
                    </BreadcrumbItem>
                  </>
                )}
              </BreadcrumbList>
            </Breadcrumb>
            {step === 1 && (
              <FieldGroup>
                <Field>
                  <FieldLabel
                    className="inline-block"
                    htmlFor="opportunity-next-step-field"
                  >
                    Opportunity Next Step{' '}
                    <span className="text-red-500">*</span>
                  </FieldLabel>
                  <Select
                    onValueChange={value => {
                      setOpportunityNextStep(value);
                      setKillReason(undefined);
                    }}
                    required
                    value={opportunityNextStep}
                  >
                    <SelectTrigger id="opportunity-next-step-field">
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      {dataEmployeesAndDealSelects.statusSelections
                        .filter(({ value }) =>
                          [
                            'killed',
                            'not_enough_information',
                            'ready_for_md_review'
                          ].includes(value)
                        )
                        .map(opportunityNextStep => (
                          <SelectItem
                            key={opportunityNextStep.id}
                            value={opportunityNextStep.value}
                          >
                            {opportunityNextStep.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </Field>
                {opportunityNextStep === 'killed' && (
                  <>
                    <Field>
                      <FieldLabel
                        className="inline-block"
                        htmlFor="kill-reason-field"
                      >
                        Kill Reason <span className="text-red-500">*</span>
                      </FieldLabel>
                      <Select
                        onValueChange={setKillReason}
                        required
                        value={killReason}
                      >
                        <SelectTrigger id="kill-reason-field">
                          <SelectValue placeholder="Select an option" />
                        </SelectTrigger>
                        <SelectContent>
                          {dataEmployeesAndDealSelects.killReasonSelections.map(
                            killReason => (
                              <SelectItem
                                key={killReason.id}
                                value={killReason.value}
                              >
                                {killReason.name}
                              </SelectItem>
                            )
                          )}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="kill-notes-field">
                        Kill Notes
                      </FieldLabel>
                      <Input
                        id="kill-notes-field"
                        onChange={event => {
                          setKillNotes(event.target.value);
                        }}
                        placeholder="Enter Value"
                        value={killNotes}
                      />
                    </Field>
                  </>
                )}
              </FieldGroup>
            )}
            {step === 2 && (
              <div className="space-y-5">
                <div>
                  <div className="text-lg font-semibold">
                    Essential Information
                  </div>
                  <Separator className="mt-1 mb-2.5" />
                  <FieldGroup className="grid grid-cols-2 gap-2.5">
                    <Field data-invalid={projectNameFieldIsInvalid}>
                      <FieldLabel
                        className="inline-block"
                        htmlFor="project-name-field"
                      >
                        Project Name <span className="text-red-500">*</span>
                      </FieldLabel>
                      <Input
                        aria-invalid={projectNameFieldIsInvalid}
                        id="project-name-field"
                        onChange={event => {
                          setProjectName(event.target.value);
                          if (projectNameFieldIsInvalid) {
                            setProjectNameFieldIsInvalid(false);
                          }
                        }}
                        placeholder="Enter Value"
                        required
                        value={projectName}
                      />
                      {projectNameFieldIsInvalid && (
                        <FieldError>Please enter a value</FieldError>
                      )}
                    </Field>
                    <Field data-invalid={smcSourceFieldIsInvalid}>
                      <FieldLabel
                        className="inline-block"
                        htmlFor="smc-source-field"
                      >
                        SMC Source <span className="text-red-500">*</span>
                      </FieldLabel>
                      <Popover
                        modal={true}
                        onOpenChange={setOpen1}
                        open={open1}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            aria-expanded={open1}
                            aria-invalid={smcSourceFieldIsInvalid}
                            className="w-full justify-between"
                            id="smc-source-field"
                            role="combobox"
                            variant="outline"
                          >
                            {smcSource?.name ?? 'Select an option'}
                            <ChevronsUpDownIcon className="opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0">
                          <Command>
                            <CommandInput
                              className="h-9"
                              placeholder="Search SMC source..."
                              required
                            />
                            <CommandList>
                              <CommandEmpty>No SMC source found.</CommandEmpty>
                              <CommandGroup>
                                {dataEmployeesAndDealSelects.hrEmployees.edges.map(
                                  edge => (
                                    <CommandItem
                                      key={edge.node.id}
                                      onSelect={() => {
                                        setSmcSource(current =>
                                          current?.id === edge.node.id
                                            ? null
                                            : {
                                                id: edge.node.id,
                                                name: edge.node.name
                                              }
                                        );
                                        setSmcSourceFieldIsInvalid(false);
                                        setOpen1(false);
                                      }}
                                      value={edge.node.name}
                                    >
                                      {edge.node.name}
                                      <CheckIcon
                                        className={cn(
                                          'ml-auto',
                                          smcSource?.id === edge.node.id
                                            ? 'opacity-100'
                                            : 'opacity-0'
                                        )}
                                      />
                                    </CommandItem>
                                  )
                                )}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      {smcSourceFieldIsInvalid && (
                        <FieldError>Please select a value</FieldError>
                      )}
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="company-name-field">
                        Company Name
                      </FieldLabel>
                      <Popover
                        modal={true}
                        onOpenChange={setOpen2}
                        open={open2}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            aria-expanded={open2}
                            className="justify-between"
                            id="company-name-field"
                            role="combobox"
                            variant="outline"
                          >
                            {companyName?.name ?? 'Select an option'}
                            <ChevronsUpDownIcon className="opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0">
                          <Command>
                            <CommandInput
                              className="h-9"
                              placeholder="Search company name..."
                            />
                            <CommandList>
                              <CommandEmpty>
                                No company name found.
                              </CommandEmpty>
                              <CommandGroup>
                                {data3.searchAccounts.map(searchAccount => (
                                  <CommandItem
                                    key={searchAccount.id}
                                    onSelect={() => {
                                      setCompanyName(current =>
                                        current?.id === searchAccount.id
                                          ? null
                                          : {
                                              id: searchAccount.id,
                                              name: searchAccount.name
                                            }
                                      );
                                      setOpen2(false);
                                    }}
                                    value={searchAccount.name}
                                  >
                                    {searchAccount.name}
                                    <CheckIcon
                                      className={cn(
                                        'ml-auto',
                                        companyName?.id === searchAccount.id
                                          ? 'opacity-100'
                                          : 'opacity-0'
                                      )}
                                    />
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="opportunity-detail-field">
                        Opportunity Detail
                      </FieldLabel>
                      <Input
                        id="opportunity-detail-field"
                        onChange={event => {
                          setOpportunityDetail(event.target.value);
                        }}
                        placeholder="Enter Value"
                        value={opportunityDetail}
                      />
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="ioi-due-date-field">
                        IOI Due Date
                      </FieldLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            className="data-[empty=true]:text-muted-foreground w-full justify-start text-left font-normal"
                            data-empty={!date}
                            id="ioi-due-date-field"
                            variant="outline"
                          >
                            <CalendarIcon />
                            {date ? (
                              format(date, 'PPP')
                            ) : (
                              <span>Pick a date</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            onSelect={setDate}
                            selected={date}
                          />
                        </PopoverContent>
                      </Popover>
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="deal-source-field">
                        Deal Source
                      </FieldLabel>
                      <Popover
                        modal={true}
                        onOpenChange={setOpen3}
                        open={open3}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            aria-expanded={open3}
                            className="justify-between"
                            id="deal-source-field"
                            role="combobox"
                            variant="outline"
                          >
                            {dealSource?.name ?? 'Select an option'}
                            <ChevronsUpDownIcon className="opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0">
                          <Command>
                            <CommandInput
                              className="h-9"
                              placeholder="Search account..."
                            />
                            <CommandList>
                              <CommandEmpty>No account found.</CommandEmpty>
                              <CommandGroup>
                                {data3.searchAccounts.map(searchAccount => (
                                  <CommandItem
                                    key={searchAccount.id}
                                    onSelect={() => {
                                      setDealSource(current =>
                                        current?.id === searchAccount.id
                                          ? null
                                          : {
                                              id: searchAccount.id,
                                              name: searchAccount.name
                                            }
                                      );
                                      setOpen3(false);
                                    }}
                                    value={searchAccount.name}
                                  >
                                    {searchAccount.name}
                                    <CheckIcon
                                      className={cn(
                                        'ml-auto',
                                        dealSource?.id === searchAccount.id
                                          ? 'opacity-100'
                                          : 'opacity-0'
                                      )}
                                    />
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="source-type-field">
                        Source Type
                      </FieldLabel>
                      <Popover
                        modal={true}
                        onOpenChange={setOpen5}
                        open={open5}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            aria-expanded={open5}
                            className="justify-between"
                            id="source-field"
                            role="combobox"
                            variant="outline"
                          >
                            {sourceType?.name ?? 'Select an option'}
                            <ChevronsUpDownIcon className="opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0">
                          <Command>
                            <CommandInput
                              className="h-9"
                              placeholder="Search source type..."
                            />
                            <CommandList>
                              <CommandEmpty>No source type found.</CommandEmpty>
                              <CommandGroup>
                                {dataEmployeesAndDealSelects.dealSourceTypeSelections.map(
                                  a => (
                                    <CommandItem
                                      key={a.id}
                                      onSelect={() => {
                                        setSourceType(current =>
                                          current?.id === a.id
                                            ? null
                                            : {
                                                id: a.id,
                                                name: a.name,
                                                value: a.value
                                              }
                                        );
                                        setOpen5(false);
                                      }}
                                      value={a.name}
                                    >
                                      {a.name}
                                      <CheckIcon
                                        className={cn(
                                          'ml-auto',
                                          sourceType?.id === a.id
                                            ? 'opacity-100'
                                            : 'opacity-0'
                                        )}
                                      />
                                    </CommandItem>
                                  )
                                )}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="smc-industry-field">
                        SMC Industry
                      </FieldLabel>
                      <Popover
                        modal={true}
                        onOpenChange={setOpen4}
                        open={open4}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            aria-expanded={open4}
                            className="justify-between"
                            id="smc-industry-field"
                            role="combobox"
                            variant="outline"
                          >
                            {smcIndustry?.name ?? 'Select an option'}
                            <ChevronsUpDownIcon className="opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0">
                          <Command>
                            <CommandInput
                              className="h-9"
                              placeholder="Search SMC industry..."
                            />
                            <CommandList>
                              <CommandEmpty>
                                No SMC industry found.
                              </CommandEmpty>
                              <CommandGroup>
                                {dataEmployeesAndDealSelects.smcIndustrySelections.map(
                                  a => (
                                    <CommandItem
                                      key={a.value}
                                      onSelect={() => {
                                        setSmcIndustry(current =>
                                          current?.value === a.value
                                            ? null
                                            : {
                                                name: a.name,
                                                value: a.value
                                              }
                                        );
                                        setOpen4(false);
                                      }}
                                      value={a.name}
                                    >
                                      {a.name}
                                      <CheckIcon
                                        className={cn(
                                          'ml-auto',
                                          smcIndustry?.value === a.value
                                            ? 'opacity-100'
                                            : 'opacity-0'
                                        )}
                                      />
                                    </CommandItem>
                                  )
                                )}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </Field>
                  </FieldGroup>
                </div>
                <div>
                  <div className="text-lg font-semibold">
                    Financial Information
                  </div>
                  <Separator className="mt-1 mb-2.5" />
                  <FieldGroup className="grid grid-cols-2 gap-2.5">
                    <Field>
                      <FieldLabel htmlFor="ebitda-field">EBITDA</FieldLabel>
                      <InputGroup>
                        <InputGroupAddon>
                          <InputGroupText>$</InputGroupText>
                        </InputGroupAddon>
                        <InputGroupInput
                          id="ebitda-field"
                          onChange={event => {
                            setLtmEbitda(event.target.value);
                          }}
                          placeholder="Enter Value"
                          value={ltmEbitda}
                        />
                        <InputGroupAddon align="inline-end">
                          <InputGroupText>USD</InputGroupText>
                        </InputGroupAddon>
                      </InputGroup>
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="reference-point-field">
                        Reference Point
                      </FieldLabel>
                      <Input
                        id="reference-point-field"
                        onChange={event => {
                          setReferencePoint(event.target.value);
                        }}
                        placeholder="Enter Value"
                        value={referencePoint}
                      />
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="total-proposed-debt-investment-field">
                        Total Proposed Debt Investment
                      </FieldLabel>
                      <InputGroup>
                        <InputGroupAddon>
                          <InputGroupText>$</InputGroupText>
                        </InputGroupAddon>
                        <InputGroupInput
                          id="total-proposed-debt-investment-field"
                          onChange={event => {
                            setTotalProposedDebtInvestment(event.target.value);
                          }}
                          placeholder="Enter Value"
                          value={totalProposedDebtInvestment}
                        />
                        <InputGroupAddon align="inline-end">
                          <InputGroupText>USD</InputGroupText>
                        </InputGroupAddon>
                      </InputGroup>
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="total-proposed-equity-investment-field">
                        Total Proposed Equity Investment
                      </FieldLabel>
                      <InputGroup>
                        <InputGroupAddon>
                          <InputGroupText>$</InputGroupText>
                        </InputGroupAddon>
                        <InputGroupInput
                          id="total-proposed-equity-investment-field"
                          onChange={event => {
                            setTotalProposedEquityInvestment(
                              event.target.value
                            );
                          }}
                          placeholder="Enter Value"
                          value={totalProposedEquityInvestment}
                        />
                        <InputGroupAddon align="inline-end">
                          <InputGroupText>USD</InputGroupText>
                        </InputGroupAddon>
                      </InputGroup>
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="sm-proposed-debt-investment-field">
                        SM Proposed Debt Investment
                      </FieldLabel>
                      <InputGroup>
                        <InputGroupAddon>
                          <InputGroupText>$</InputGroupText>
                        </InputGroupAddon>
                        <InputGroupInput
                          id="sm-proposed-debt-investment-field"
                          onChange={event => {
                            setSmProjectedDebtInvestment(event.target.value);
                          }}
                          placeholder="Enter Value"
                          value={smProjectedDebtInvestment}
                        />
                        <InputGroupAddon align="inline-end">
                          <InputGroupText>USD</InputGroupText>
                        </InputGroupAddon>
                      </InputGroup>
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="sm-proposed-equity-investment-field">
                        SM Proposed Equity Investment
                      </FieldLabel>
                      <InputGroup>
                        <InputGroupAddon>
                          <InputGroupText>$</InputGroupText>
                        </InputGroupAddon>
                        <InputGroupInput
                          id="sm-proposed-equity-investment-field"
                          onChange={event => {
                            setSmProjectedEquityInvestment(event.target.value);
                          }}
                          placeholder="Enter Value"
                          value={smProjectedEquityInvestment}
                        />
                        <InputGroupAddon align="inline-end">
                          <InputGroupText>USD</InputGroupText>
                        </InputGroupAddon>
                      </InputGroup>
                    </Field>
                  </FieldGroup>
                </div>
                <div className="pb-5">
                  <div className="text-lg font-semibold">
                    Documentation & Correspondence
                  </div>
                  <Separator className="mt-1 mb-2.5" />
                  <div className="flex flex-col gap-2.5">
                    <div className="grid grid-cols-3 gap-2.5">
                      <Field>
                        <FieldLabel htmlFor="deal-cim-field">CIM</FieldLabel>
                        <Input
                          accept=".pdf,application/pdf"
                          id="deal-cim-field"
                          onChange={event => {
                            setDealCimFile(event.target.files?.[0] ?? null);
                          }}
                          ref={dealCimInputRef}
                          type="file"
                        />
                      </Field>
                      <Field>
                        <FieldLabel htmlFor="deal-nda-field">NDA</FieldLabel>
                        <Input
                          accept=".pdf,application/pdf"
                          id="deal-nda-field"
                          onChange={event => {
                            setDealNdaFile(event.target.files?.[0] ?? null);
                          }}
                          ref={dealNdaInputRef}
                          type="file"
                        />
                      </Field>
                      <Field>
                        <FieldLabel htmlFor="deal-teaser-field">
                          Deal Teaser
                        </FieldLabel>
                        <Input
                          accept=".pdf,application/pdf"
                          id="deal-teaser-field"
                          onChange={event => {
                            setDealTeaserFile(event.target.files?.[0] ?? null);
                          }}
                          ref={dealTeaserInputRef}
                          type="file"
                        />
                      </Field>
                    </div>
                    <Field>
                      <FieldLabel htmlFor="email-body-field">
                        Email Body
                      </FieldLabel>
                      <Textarea
                        id="email-body-field"
                        onChange={event => {
                          setEmailBody(event.target.value);
                        }}
                        placeholder="Enter Value"
                        value={emailBody}
                      />
                    </Field>
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          {step === 1 && (
            <Button
              disabled={
                opportunityNextStep === undefined ||
                (opportunityNextStep === 'killed' && killReason === undefined)
              }
              onClick={() => {
                setStep(2);
              }}
            >
              Next
            </Button>
          )}
          {step === 2 && (
            <Button
              disabled={isCreatingOpportunity}
              onClick={() => {
                void handleLogNewDealButtonClick();
              }}
              type="submit"
            >
              {isCreatingOpportunity ? (
                <>
                  <Spinner />
                  Loading...
                </>
              ) : (
                'Log New Deal'
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
